import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext, AppSettingsContext } from '../App';
import Layout from '../components/Layout';
import Footer from '../components/Footer';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  ChevronDown, 
  ChevronRight,
  BookOpen,
  PlusCircle,
  BarChart3,
  History,
  Target,
  Sparkles,
  Users,
  Trophy,
  DollarSign,
  Brain,
  TrendingUp,
  Filter,
  Download,
  Bell,
  Settings,
  CreditCard,
  Star,
  Lightbulb,
  HelpCircle,
  CheckCircle,
  ArrowRight,
  Layers
} from 'lucide-react';

const UserGuide = () => {
  const { user } = useContext(AuthContext);
  const appSettings = useContext(AppSettingsContext);
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({
    betting: true,
    tipster: false,
    strategies: false,
    predictions: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const Section = ({ id, title, icon: Icon, color, children }) => (
    <Card className="glass-dark border-white/10 mb-4 overflow-hidden">
      <button
        onClick={() => toggleSection(id)}
        className="w-full text-left"
      >
        <CardHeader className="cursor-pointer hover:bg-white/5 transition-colors">
          <CardTitle className="text-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${color}`}>
                <Icon className="w-5 h-5" />
              </div>
              <span className="text-lg">{title}</span>
            </div>
            {expandedSections[id] ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </CardTitle>
        </CardHeader>
      </button>
      {expandedSections[id] && (
        <CardContent className="border-t border-white/10 pt-6">
          {children}
        </CardContent>
      )}
    </Card>
  );

  const SubSection = ({ title, icon: Icon, children }) => (
    <div className="mb-6 last:mb-0">
      <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-blue-400" />}
        {title}
      </h3>
      <div className="text-gray-300 text-sm space-y-2 pl-6">
        {children}
      </div>
    </div>
  );

  const Step = ({ number, children }) => (
    <div className="flex items-start gap-3 mb-2">
      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 text-xs font-bold flex items-center justify-center">
        {number}
      </span>
      <p className="text-gray-300">{children}</p>
    </div>
  );

  const Tip = ({ children }) => (
    <div className="flex items-start gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/20 mt-3">
      <Lightbulb className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
      <p className="text-green-300 text-sm">{children}</p>
    </div>
  );

  const Note = ({ children }) => (
    <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 mt-3">
      <HelpCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
      <p className="text-blue-300 text-sm">{children}</p>
    </div>
  );

  // Guest header component for non-logged-in users
  const GuestHeader = () => (
    <nav className="fixed top-0 w-full z-50 glass-dark border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
            {appSettings?.app_logo_url ? (
              <img 
                src={appSettings.app_logo_url} 
                alt={appSettings?.app_name || 'BettingTracker.Pro'} 
                className="h-10 max-w-[200px] object-contain"
              />
            ) : (
              <>
                <TrendingUp className="w-8 h-8 text-blue-400" />
                <span className="text-lg sm:text-xl font-bold text-white whitespace-nowrap">{appSettings?.app_name || 'BettingTracker.Pro'}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-4">
            <Link to="/blog" className="text-gray-300 hover:text-white transition-colors font-medium">
              Blog
            </Link>
            <Button onClick={() => navigate('/login')} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full">
              Sign In
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );

  // Wrapper component based on user state
  const PageWrapper = ({ children }) => {
    if (user) {
      return <Layout>{children}</Layout>;
    }
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
        <GuestHeader />
        <div className="pt-16">
          {children}
        </div>
        <Footer />
      </div>
    );
  };

  return (
    <PageWrapper>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-purple-500/30">
                <BookOpen className="w-8 h-8 text-purple-400" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-white">User Guide</h1>
                <p className="text-gray-400">Learn how to use all features of BettingTracker.Pro</p>
              </div>
            </div>
            <p className="text-gray-300">
              Welcome to the comprehensive guide for BettingTracker.Pro. Click on each section below to learn how to use our features effectively.
            </p>
          </div>

          {/* Quick Navigation */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
            {[
              { id: 'betting', label: 'Betting Tracker', icon: BarChart3, color: 'text-green-400' },
              { id: 'tipster', label: 'Tipster Area', icon: Users, color: 'text-orange-400' },
              { id: 'strategies', label: 'Strategies', icon: Lightbulb, color: 'text-yellow-400' },
              { id: 'predictions', label: 'AI Predictions', icon: Brain, color: 'text-purple-400' }
            ].map(item => (
              <button
                key={item.id}
                onClick={() => {
                  setExpandedSections(prev => ({ ...prev, [item.id]: true }));
                  // Delay scroll to allow section to expand first
                  setTimeout(() => {
                    document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="p-3 rounded-lg glass border border-white/10 hover:border-white/20 transition-colors text-center"
              >
                <item.icon className={`w-6 h-6 ${item.color} mx-auto mb-2`} />
                <span className="text-white text-sm font-medium">{item.label}</span>
              </button>
            ))}
          </div>

          {/* Section 1: Betting Tracker */}
          <div id="betting" className="scroll-mt-24">
            <Section 
              id="betting" 
              title="Betting Tracker" 
              icon={BarChart3}
              color="bg-green-500/20 text-green-400"
            >
              <SubSection title="Adding a Single Bet" icon={PlusCircle}>
                <p>Track individual bets to analyze your performance over time.</p>
                <Step number="1">Navigate to <strong>"Add Bet"</strong> from the main menu.</Step>
                <Step number="2">Select <strong>"Single Bet"</strong> mode (default).</Step>
                <Step number="3">Select the <strong>League</strong> from the dropdown (or type to search).</Step>
                <Step number="4">Choose the <strong>Home Team</strong> and <strong>Away Team</strong>.</Step>
                <Step number="5">Choose the <strong>Market</strong> (e.g., Match Winner, Over/Under 2.5 Goals, BTTS).</Step>
                <Step number="6">Enter the <strong>Odds</strong> you received.</Step>
                <Step number="7">Select your <strong>Bookmaker</strong> and enter your <strong>Stake</strong> amount.</Step>
                <Step number="8">Optionally add a <strong>Tipster</strong> and <strong>Strategy</strong>.</Step>
                <Step number="9">Select the <strong>Bet Date</strong> and click <strong>"Add Single Bet"</strong>.</Step>
                <Tip>You can add custom bookmakers and markets that aren't in the default list. They'll appear with a ⭐ icon.</Tip>
              </SubSection>

              <SubSection title="Adding an Accumulator" icon={Layers}>
                <p>Track multi-selection bets (accumulators, parlays, combos).</p>
                <Step number="1">Navigate to <strong>"Add Bet"</strong> and click <strong>"Accumulator"</strong>.</Step>
                <Step number="2">Add your first selection: League, Teams, Market, and Odds.</Step>
                <Step number="3">Click <strong>"Add to Accumulator"</strong> to add the selection.</Step>
                <Step number="4">Repeat to add more selections (minimum 2 required).</Step>
                <Step number="5">The <strong>Combined Odds</strong> are calculated automatically.</Step>
                <Step number="6">Fill in common details: Bookmaker, Stake, Tipster, Strategy.</Step>
                <Step number="7">Click <strong>"Add Accumulator"</strong> to save.</Step>
                <Note>In accumulators, each selection can be individually marked as Won, Lost, or Void. The overall result is calculated automatically.</Note>
              </SubSection>

              <SubSection title="Bet History" icon={History}>
                <p>View and manage all your tracked bets.</p>
                <Step number="1">Go to <strong>"Bet History"</strong> from the menu.</Step>
                <Step number="2">Your bets are displayed with all details including potential return.</Step>
                <Step number="3">Use the <strong>Result</strong> dropdown to update bet outcomes (Won, Lost, or Void).</Step>
                <Step number="4">For <strong>Accumulators</strong>, click the expand button to see all selections.</Step>
                <Step number="5">Update each selection's result individually - overall result calculates automatically.</Step>
                <Step number="6">Use <strong>Filter by Bet Type</strong> to show only Singles or Accumulators.</Step>
                <Note>Void bets are treated as refunded. In accumulators, void selections reduce the combined odds.</Note>
              </SubSection>

              <SubSection title="Reports & Analytics" icon={BarChart3}>
                <p>Analyze your betting performance with detailed statistics.</p>
                <Step number="1">Navigate to <strong>"Reports"</strong> from the main menu.</Step>
                <Step number="2">View your overall statistics: Total Bets, Win Rate, Total Staked, and ROI.</Step>
                <Step number="3">Use <strong>filters</strong> to narrow down your analysis by League, Tipster, Strategy, Bookmaker, Market, or Date Range.</Step>
                <Step number="4">Scroll down to see <strong>performance charts</strong> showing profit over time and win rate trends.</Step>
                <Step number="5">Premium users can <strong>export data</strong> in CSV, Excel, or PDF format.</Step>
                <Tip>Filter by specific leagues or markets to identify where you perform best!</Tip>
              </SubSection>

              <SubSection title="AI Betting Analysis" icon={Sparkles}>
                <p>Get personalized recommendations based on your betting history.</p>
                <Step number="1">The AI Analysis card appears at the top of your <strong>Reports</strong> page.</Step>
                <Step number="2">Track at least <strong>10 bets</strong> to unlock basic analysis.</Step>
                <Step number="3">Track <strong>20+ bets</strong> for high-confidence recommendations.</Step>
                <Step number="4">View analysis for: <strong>Best Market</strong>, <strong>Best League</strong>, <strong>Best Odds Range</strong>, <strong>Best Tipster</strong>, and <strong>Best Strategy</strong>.</Step>
                <Step number="5">Click <strong>"Show Detailed Analysis"</strong> to see complete performance tables for each category.</Step>
                <Note>Analysis is based on ROI (Return on Investment). A positive ROI means you're profitable in that category!</Note>
              </SubSection>

              <SubSection title="Filtering Your Data" icon={Filter}>
                <p>Use powerful filters to analyze specific segments of your bets.</p>
                <Step number="1">In <strong>Reports</strong>, you'll see filter options at the top.</Step>
                <Step number="2"><strong>League Filter</strong>: Analyze bets in specific competitions (Premium feature).</Step>
                <Step number="3"><strong>Tipster Filter</strong>: Track which tipsters give you the best results (Premium feature).</Step>
                <Step number="4"><strong>Strategy Filter</strong>: See how different strategies perform.</Step>
                <Step number="5"><strong>Bookmaker Filter</strong>: Compare your results across different bookmakers.</Step>
                <Step number="6"><strong>Market Filter</strong>: Identify your most profitable market types.</Step>
                <Step number="7"><strong>Date Range</strong>: Analyze performance for specific time periods.</Step>
                <Step number="8">Click <strong>"Clear Filters"</strong> to reset all filters.</Step>
              </SubSection>

              <SubSection title="Exporting Data" icon={Download}>
                <p>Export your betting data for external analysis (Premium feature).</p>
                <Step number="1">Click the <strong>"Export Data"</strong> button in Reports.</Step>
                <Step number="2">Choose your format: <strong>CSV</strong> (spreadsheet), <strong>Excel</strong>, or <strong>PDF</strong> (printable report).</Step>
                <Step number="3">Use the <strong>Print Report</strong> option for a physical copy.</Step>
                <Step number="4">Exports include all your filtered data with statistics.</Step>
                <Note>Export is available for Premium and Tipster plan users.</Note>
              </SubSection>
            </Section>
          </div>

          {/* Section 2: Tipster Features */}
          <div id="tipster" className="scroll-mt-24">
            <Section 
              id="tipster" 
              title="Tipster Opportunity" 
              icon={Users}
              color="bg-orange-500/20 text-orange-400"
            >
              <SubSection title="Becoming a Tipster" icon={Trophy}>
                <p>Share your betting expertise and earn money from subscribers.</p>
                <Step number="1">Navigate to <strong>"Become a Tipster"</strong> from the menu or landing page.</Step>
                <Step number="2">Subscribe to the <strong>Tipster Plan</strong> to unlock tipster features.</Step>
                <Step number="3">Once subscribed, you can create your <strong>Tipster Service</strong>.</Step>
                <Step number="4">Set your service <strong>name</strong>, <strong>description</strong>, and <strong>pricing</strong>.</Step>
                <Step number="5">Add your <strong>PayPal email</strong> in My Profile to receive payments.</Step>
                <Tip>Write a compelling service description that highlights your expertise and track record!</Tip>
              </SubSection>

              <SubSection title="Creating Tips" icon={Lightbulb}>
                <p>Publish tips for your subscribers to follow.</p>
                <Step number="1">Go to <strong>"Create Tip"</strong> in your Tipster Dashboard.</Step>
                <Step number="2">Select the <strong>League</strong> and <strong>Match</strong> for your tip.</Step>
                <Step number="3">Choose the <strong>Market</strong> (e.g., Match Winner, Over/Under).</Step>
                <Step number="4">Enter your <strong>prediction</strong> and the <strong>odds</strong>.</Step>
                <Step number="5">Add your <strong>analysis</strong> explaining why you recommend this bet.</Step>
                <Step number="6">Set the tip as <strong>Free</strong> (visible to all) or <strong>Premium</strong> (subscribers only).</Step>
                <Step number="7">Click <strong>"Publish Tip"</strong> to make it available.</Step>
                <Note>Offer some free tips to attract potential subscribers and showcase your expertise.</Note>
              </SubSection>

              <SubSection title="Managing Your Tips" icon={Settings}>
                <p>Update and track your published tips.</p>
                <Step number="1">View all your tips in the <strong>Tipster Dashboard</strong>.</Step>
                <Step number="2">Update tip <strong>results</strong> after matches conclude (Won, Lost, Void).</Step>
                <Step number="3">Your <strong>statistics</strong> update automatically based on results.</Step>
                <Step number="4">Subscribers can see your <strong>win rate</strong> and <strong>ROI</strong>.</Step>
                <Step number="5">Delete tips if needed (only before results are set).</Step>
              </SubSection>

              <SubSection title="Your Tipster Statistics" icon={TrendingUp}>
                <p>Monitor your performance and attract more subscribers.</p>
                <Step number="1">Your public <strong>Tipster Profile</strong> shows your statistics.</Step>
                <Step number="2">Key metrics displayed: <strong>Total Tips</strong>, <strong>Win Rate</strong>, <strong>ROI</strong>, <strong>Followers</strong>.</Step>
                <Step number="3">Your <strong>recent tips</strong> history is visible to potential subscribers.</Step>
                <Step number="4">Subscribers can leave <strong>reviews</strong> and ratings.</Step>
                <Step number="5">Good statistics and reviews help attract more subscribers!</Step>
                <Tip>Consistently provide quality tips with good analysis to build your reputation.</Tip>
              </SubSection>

              <SubSection title="Earning Money" icon={DollarSign}>
                <p>Get paid for your tipster services.</p>
                <Step number="1">Set your <strong>subscription price</strong> in your service settings.</Step>
                <Step number="2">Users subscribe to your service and pay the set price.</Step>
                <Step number="3">Earnings are credited to your <strong>Tipster Wallet</strong>.</Step>
                <Step number="4">Add your <strong>PayPal email</strong> in My Profile to receive payments.</Step>
                <Step number="5">Request a <strong>withdrawal</strong> when you want to cash out.</Step>
                <Step number="6">Withdrawals are processed to your PayPal account.</Step>
                <Note>Make sure your PayPal email is correct before requesting withdrawals.</Note>
              </SubSection>

              <SubSection title="Following Tipsters" icon={Star}>
                <p>As a user, you can follow and subscribe to tipsters.</p>
                <Step number="1">Browse tipsters in the <strong>"Tipster Services"</strong> section.</Step>
                <Step number="2">View tipster <strong>profiles</strong> with their statistics and reviews.</Step>
                <Step number="3"><strong>Follow</strong> tipsters for free to get notified of their free tips.</Step>
                <Step number="4"><strong>Subscribe</strong> to access their premium tips (paid).</Step>
                <Step number="5">View purchased tips in your <strong>Purchased Tips</strong> section.</Step>
                <Step number="6">Leave <strong>reviews</strong> to help other users find good tipsters.</Step>
              </SubSection>
            </Section>
          </div>

          {/* Section 3: Betting Strategies */}
          <div id="strategies" className="scroll-mt-24">
            <Section 
              id="strategies" 
              title="Knowledge Base - Betting Strategies" 
              icon={Lightbulb}
              color="bg-yellow-500/20 text-yellow-400"
            >
              <SubSection title="Accessing Strategies" icon={BookOpen}>
                <p>Learn proven betting strategies from our knowledge base.</p>
                <Step number="1">Navigate to <strong>"Betting Strategies"</strong> from the menu.</Step>
                <Step number="2">Browse through the list of available strategies.</Step>
                <Step number="3">Each strategy shows a <strong>title</strong>, <strong>description</strong>, and <strong>category</strong>.</Step>
                <Step number="4">Click on a strategy to read the full detailed guide.</Step>
                <Step number="5">Strategies include examples and step-by-step implementation guides.</Step>
              </SubSection>

              <SubSection title="Strategy Categories" icon={Target}>
                <p>Strategies are organized into different categories.</p>
                <ul className="list-disc list-inside space-y-1 text-gray-300">
                  <li><strong>Value Betting</strong> - Finding bets where odds are higher than true probability</li>
                  <li><strong>Bankroll Management</strong> - Managing your betting budget effectively</li>
                  <li><strong>Market-Specific</strong> - Strategies for Over/Under, BTTS, etc.</li>
                  <li><strong>League-Specific</strong> - Strategies optimized for certain competitions</li>
                  <li><strong>Advanced</strong> - Complex strategies for experienced bettors</li>
                </ul>
              </SubSection>

              <SubSection title="Applying Strategies" icon={CheckCircle}>
                <p>How to use strategies with the bet tracker.</p>
                <Step number="1">Read and understand a strategy from the knowledge base.</Step>
                <Step number="2">When adding a bet that follows a strategy, enter the <strong>Strategy name</strong>.</Step>
                <Step number="3">Track all bets using that strategy consistently.</Step>
                <Step number="4">Use <strong>Reports filters</strong> to analyze how each strategy performs.</Step>
                <Step number="5">The <strong>AI Analysis</strong> will show your best-performing strategies.</Step>
                <Tip>Stick to one or two strategies initially and master them before trying others.</Tip>
              </SubSection>

              <SubSection title="Liking Strategies" icon={Star}>
                <p>Save your favorite strategies for easy access.</p>
                <Step number="1">Click the <strong>heart/like icon</strong> on any strategy.</Step>
                <Step number="2">Liked strategies appear at the top of your list.</Step>
                <Step number="3">See how many other users have liked each strategy.</Step>
              </SubSection>
            </Section>
          </div>

          {/* Section 4: AI Predictions */}
          <div id="predictions" className="scroll-mt-24">
            <Section 
              id="predictions" 
              title="AI Football Predictions" 
              icon={Brain}
              color="bg-purple-500/20 text-purple-400"
            >
              <SubSection title="Accessing AI Predictions" icon={Brain}>
                <p>Get AI-powered football predictions for free.</p>
                <Step number="1">Navigate to <strong>"AI Predictions"</strong> from the menu.</Step>
                <Step number="2">All users can access daily AI-generated predictions.</Step>
                <Step number="3">Predictions are generated for top leagues worldwide.</Step>
                <Step number="4">New predictions are available daily (auto-generated).</Step>
                <Note>Standard AI Predictions are free for all users. Premium users get additional features: Prediction Statistics and Personalized Predictions.</Note>
              </SubSection>

              <SubSection title="Types of Predictions" icon={Target}>
                <p>AI generates different types of betting predictions.</p>
                <ul className="list-disc list-inside space-y-2 text-gray-300">
                  <li><strong>Singles</strong> - Individual match predictions with detailed analysis</li>
                  <li><strong>Doubles</strong> - Two-match accumulators with combined odds</li>
                  <li><strong>Trebles</strong> - Three-match accumulators for higher odds</li>
                </ul>
                <div className="mt-3">
                  <p className="font-medium text-white mb-2">Available Markets:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-300">
                    <li>Match Winner (1X2)</li>
                    <li>Over/Under Goals (2.5, 1.5, 3.5)</li>
                    <li>Both Teams to Score (BTTS)</li>
                    <li>Double Chance (1X, X2, 12)</li>
                    <li>Combined Markets (e.g., Home Win & Over 2.5)</li>
                  </ul>
                </div>
              </SubSection>

              <SubSection title="Understanding Predictions" icon={TrendingUp}>
                <p>Each prediction includes detailed information.</p>
                <Step number="1"><strong>Match Details</strong> - Teams, league, and kick-off time.</Step>
                <Step number="2"><strong>Market</strong> - The type of bet recommended.</Step>
                <Step number="3"><strong>Prediction</strong> - The specific outcome predicted.</Step>
                <Step number="4"><strong>Odds</strong> - The odds at prediction time.</Step>
                <Step number="5"><strong>Confidence</strong> - How confident the AI is in the prediction.</Step>
                <Step number="6"><strong>Analysis</strong> - Detailed reasoning behind the prediction.</Step>
              </SubSection>

              <SubSection title="Prediction Statistics" icon={BarChart3}>
                <p>Track the performance of AI predictions over time (Premium feature).</p>
                <Step number="1">Scroll down on the AI Predictions page to see <strong>Statistics</strong>.</Step>
                <Step number="2">View stats for each prediction type: Singles, Doubles, Trebles.</Step>
                <Step number="3">Key metrics: <strong>Total Predictions</strong>, <strong>Win Rate</strong>, <strong>ROI</strong>, <strong>Avg Odds</strong>.</Step>
                <Step number="4">See breakdown: <strong>Won</strong>, <strong>Lost</strong>, <strong>Void</strong>, <strong>Pending</strong>.</Step>
                <Step number="5">Check the <strong>Prediction History</strong> to see past results.</Step>
                <Tip>Use the prediction statistics to decide which prediction types work best for you!</Tip>
                <Note>Prediction Statistics require a Premium or Tipster subscription.</Note>
              </SubSection>

              <SubSection title="Void Predictions" icon={HelpCircle}>
                <p>Understanding how void matches are handled.</p>
                <Step number="1">Matches can be voided due to: Postponement, Cancellation, or Abandonment.</Step>
                <Step number="2">Void matches are shown with a <strong>yellow indicator</strong>.</Step>
                <Step number="3">In accumulators, void matches are treated as <strong>odds 1.00</strong>.</Step>
                <Step number="4">The remaining legs determine if the bet wins or loses.</Step>
                <Step number="5">Original odds are shown struck-through with adjusted odds displayed.</Step>
              </SubSection>

              <SubSection title="Email Notifications" icon={Bell}>
                <p>Get predictions delivered to your inbox.</p>
                <Step number="1">Go to <strong>"My Profile"</strong> settings.</Step>
                <Step number="2">Enable <strong>"AI Prediction Notifications"</strong>.</Step>
                <Step number="3">New predictions will be emailed to you when generated.</Step>
                <Step number="4">Emails include all prediction details and analysis.</Step>
              </SubSection>

              <SubSection title="Personalized Predictions" icon={Sparkles}>
                <p>Create custom AI predictions tailored to your preferences (Premium feature).</p>
                <Step number="1">Navigate to <strong>"AI Predictions"</strong> and look for <strong>"Personalized Predictions"</strong>.</Step>
                <Step number="2">Click <strong>"Customize Your Prediction"</strong> to open the options.</Step>
                <Step number="3">Choose your <strong>Total Odds Range</strong>: 1-2 (safe), 2-3 (moderate), 3-5 (medium), 5-8 (risky), 8-12 (high-risk), or 12+ (very high-risk).</Step>
                <Step number="4">Set <strong>Minimum Single Odds</strong>: Options from {">"} 1.20 to {">"} 2.00 for individual selections.</Step>
                <Step number="5">Select one or more <strong>Markets</strong>: Match Winner, Over/Under Goals, BTTS, Double Chance, or Combined Markets.</Step>
                <Step number="6">Optionally choose specific <strong>Leagues</strong> from your synced leagues.</Step>
                <Step number="7">Click <strong>"Generate Personalized Prediction"</strong> to create your custom prediction.</Step>
                <Step number="8">View the generated prediction with matches, analysis, and combined odds.</Step>
                <Tip>Start with safer odds ranges (1-2 or 2-3) and gradually increase as you learn the patterns!</Tip>
                <Note>Personalized Predictions require a Premium or Tipster subscription. The AI analyzes current matches and finds the best combinations based on your criteria.</Note>
              </SubSection>
            </Section>
          </div>

          {/* Additional Help */}
          <Card className="glass-dark border-white/10 mt-8">
            <CardContent className="pt-6">
              <div className="text-center">
                <HelpCircle className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Need More Help?</h3>
                <p className="text-gray-400 mb-4">
                  If you have any questions or need assistance, feel free to contact our support team.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a 
                    href="mailto:support@bettingtracker.pro" 
                    className="text-blue-400 hover:text-blue-300 flex items-center gap-2"
                  >
                    <ArrowRight className="w-4 h-4" />
                    support@bettingtracker.pro
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </PageWrapper>
  );
};

export default UserGuide;
